import type { AutomationType } from '@playbook-technologies/common-enums';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Switch,
  Typography,
} from '@mui/material';
import { useRef, useState } from 'react';

import { AUTOMATION_COPY } from '../automation-copy';
import { useAutomationsViewModel } from '../view-model/Automations.viewModel';
import * as styles from './Automations.view.css';

export const AutomationsView = ({ creatorId }: { creatorId: number }) => {
  const vm = useAutomationsViewModel(creatorId);
  const [confirming, setConfirming] = useState<AutomationType | null>(null);
  const switchRefs = useRef<
    Partial<Record<AutomationType, HTMLInputElement | null>>
  >({});

  if (vm.type === 'not-fetched' || vm.type === 'fetching') {
    return <Skeleton variant="rounded" height={112} />;
  }
  if (vm.type === 'fetch-error') {
    return (
      <Alert
        severity="error"
        action={
          <Button onClick={() => void vm.value.retry()}>Try again</Button>
        }
      >
        {vm.value.message}
      </Alert>
    );
  }

  const rowToDisable = vm.value.rows.find(
    (row) => row.automationType === confirming,
  );
  const closeModal = () => {
    const type = confirming;
    setConfirming(null);
    if (type) requestAnimationFrame(() => switchRefs.current[type]?.focus());
  };

  return (
    <div className={styles.container}>
      {vm.value.rows.map((row) => {
        const descriptionId = `automation-${row.automationType}-description`;
        return (
          <div className={styles.row} key={row.automationType}>
            <div className={styles.copy}>
              <Typography variant="h6">{row.label}</Typography>
              <Typography id={descriptionId}>{row.description}</Typography>
              {row.error ? (
                <div>
                  <Typography className={styles.error} role="alert">
                    {row.error}
                  </Typography>
                  {row.retry ? (
                    <Button onClick={() => void row.retry?.()}>
                      Try again
                    </Button>
                  ) : null}
                </div>
              ) : null}
              <span className={styles.status} aria-live="polite">
                {row.status ?? ''}
              </span>
            </div>
            <Switch
              inputRef={(element) => {
                switchRefs.current[row.automationType] = element;
              }}
              checked={row.enabled}
              disabled={row.pending}
              inputProps={{
                'aria-label': `${row.label}: ${row.enabled ? 'on' : 'off'}`,
                'aria-describedby': descriptionId,
              }}
              onChange={() =>
                row.enabled
                  ? setConfirming(row.automationType)
                  : void vm.value.update(row.automationType, true)
              }
            />
          </div>
        );
      })}
      <Dialog
        open={confirming !== null}
        onClose={rowToDisable?.pending ? undefined : closeModal}
      >
        <DialogTitle>
          {rowToDisable
            ? AUTOMATION_COPY[rowToDisable.automationType].disableConfirmation
                .title
            : ''}
        </DialogTitle>
        <DialogContent>
          {rowToDisable
            ? AUTOMATION_COPY[rowToDisable.automationType].disableConfirmation
                .body
            : ''}
        </DialogContent>
        <DialogActions>
          <Button disabled={rowToDisable?.pending} onClick={closeModal}>
            Cancel
          </Button>
          <Button
            color="error"
            disabled={!rowToDisable || rowToDisable.pending}
            onClick={async () => {
              if (!rowToDisable) return;
              await vm.value.update(rowToDisable.automationType, false);
              closeModal();
            }}
          >
            Turn off automation
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
